/**
 * Supabase client and service functions
 * 
 * This module provides the Supabase client and service functions for interacting
 * with the database. It includes error handling and fallbacks for missing environment
 * variables to ensure the application can render even without backend connectivity.
 * 
 * Features:
 * - Graceful fallback to demo mode when credentials are missing
 * - Mock data for development and testing without a real database
 * - Comprehensive error handling to prevent application crashes
 * - Type-safe API responses with consistent error formats
 */
import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { config } from '@/shared/utils/config';
import { 
  Guest, 
  Nomination, 
  Application, 
  Location, 
  GuestStatus, 
  ApprovalStatus 
} from '@/types/models/schema';
import { ApiResponse } from '@/types/api';

// Initialize Supabase client with graceful fallback for missing keys
const supabaseUrl = config.supabase.url;
const supabaseAnonKey = config.supabase.anonKey;

// Track if we're in demo mode (no real backend connection)
export const isDemoMode = !supabaseUrl || !supabaseAnonKey || supabaseUrl === 'https://example.supabase.co';

// Add a more visible console message for demo mode to help with debugging
if (isDemoMode) {
  console.warn('%c[DEMO MODE ACTIVE]', 'background: #FFA500; color: #000; font-weight: bold; padding: 2px 4px; border-radius: 2px;', 
    'Running with mock data because Supabase credentials are missing or using example URL. All database operations will use local mock data.');
}

// Create client with fallback for missing credentials
// Use a try-catch block to handle potential initialization errors
let supabase: SupabaseClient;
try {
  supabase = createClient(
    supabaseUrl || 'https://example.supabase.co', 
    supabaseAnonKey || 'demo-mode-key'
  );
} catch (error) {
  console.error('Failed to initialize Supabase client:', error);
  // Create a mock client that won't throw errors when methods are called
  supabase = {
    from: () => ({
      select: () => ({ data: [], error: null }),
      insert: () => ({ data: [], error: null }),
      update: () => ({ data: [], error: null }),
      delete: () => ({ data: null, error: null }),
      eq: () => ({ data: null, error: null }),
      single: () => ({ data: null, error: null }),
      order: () => ({ data: [], error: null }),
      range: () => ({ data: [], error: null }),
      limit: () => ({ data: [], error: null }),
      or: () => ({ data: [], error: null }),
      not: () => ({ data: [], error: null })
    }),
    auth: {
      getUser: () => Promise.resolve({ data: { user: null }, error: null }),
      signOut: () => Promise.resolve({ error: null })
    }
  } as unknown as SupabaseClient;
}

export { supabase };

// Default location for when a required location is missing
const defaultLocation: Location = {
  lat: 0,
  lng: 0,
  name: 'Unknown Location'
};

/**
 * Ensures a location object is valid and complete
 * @param location - The location object to validate
 * @returns A valid Location object
 */
const ensureValidLocation = (location: Partial<Location> | null | undefined): Location => {
  if (!location) return defaultLocation;
  
  // Ensure all required fields exist
  return {
    lat: typeof location.lat === 'number' ? location.lat : 0,
    lng: typeof location.lng === 'number' ? location.lng : 0,
    name: typeof location.name === 'string' ? location.name : 'Unknown Location'
  };
};

// Mock data for demo mode
const mockGuests: Guest[] = [
  {
    id: 'mock-1',
    name: 'Jane Smith',
    email: 'jane@example.com',
    company: 'Tech Innovations',
    title: 'CTO',
    bio: 'Technology leader with 15+ years experience',
    status: 'confirmed',
    location: { lat: 37.7749, lng: -122.4194, name: 'San Francisco, CA' },
    created_at: new Date('2023-01-15T00:00:00Z'),
    updated_at: new Date('2023-01-15T00:00:00Z')
  },
  {
    id: 'mock-2',
    name: 'John Doe',
    email: 'john@example.com',
    company: 'Future Finance',
    title: 'CEO',
    bio: 'Entrepreneur and finance expert',
    status: 'confirmed', // Changed from 'scheduled' to match enum
    location: { lat: 40.7128, lng: -74.0060, name: 'New York, NY' },
    created_at: new Date('2023-02-20T00:00:00Z'),
    updated_at: new Date('2023-02-20T00:00:00Z')
  }
];

const mockNominations: Nomination[] = [
  {
    id: 'mock-nom-1',
    nominator_name: 'Alex Johnson',
    nominator_email: 'alex@example.com',
    nominee_name: 'Sarah Williams',
    nominee_email: 'sarah@example.com',
    nominee_company: 'Data Insights',
    nominee_title: 'Data Scientist',
    reason: 'Leading expert in AI and machine learning',
    status: 'pending',
    created_at: new Date('2023-03-10'),
    location: { lat: 34.0522, lng: -118.2437, name: 'Los Angeles, CA' }
  }
];

const mockApplications: Application[] = [
  {
    id: 'mock-app-1',
    applicant_name: 'Michael Brown',
    applicant_email: 'michael@example.com',
    company: 'Green Solutions',
    title: 'Sustainability Director',
    bio: 'Working on climate tech solutions for 10+ years',
    status: 'pending',
    created_at: new Date('2023-04-05'),
    location: { lat: 47.6062, lng: -122.3321, name: 'Seattle, WA' }
  }
];

// Guest-related functions
export const guestService = {
  // Get all guests
  async getAllGuests(): Promise<ApiResponse<Guest[]>> {
    try {
      // Return mock data in demo mode
      if (isDemoMode) {
        return { data: mockGuests, error: null };
      }
      
      const { data, error } = await supabase
        .from('guests')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return { data: data as Guest[], error: null };
    } catch (error) {
      console.error('Error fetching guests:', error);
      return { data: isDemoMode ? mockGuests : [], error: error instanceof Error ? error : new Error('Unknown error') };
    }
  },
  
  // Get guest by ID
  async getGuestById(id: string): Promise<ApiResponse<Guest>> {
    try {
      // Return mock data in demo mode
      if (isDemoMode) {
        const guest = mockGuests.find(g => g.id === id);
        if (guest) {
          return { data: guest, error: null };
        }
        throw new Error(`Guest with ID ${id} not found`);
      }
      
      const { data, error } = await supabase
        .from('guests')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      return { data: data as Guest, error: null };
    } catch (error) {
      console.error(`Error fetching guest with ID ${id}:`, error);
      return { data: null, error: error instanceof Error ? error : new Error('Unknown error') };
    }
  },
  
  // Create guest
  async createGuest(guestData: Omit<Guest, 'id' | 'created_at' | 'updated_at'>): Promise<ApiResponse<Guest>> {
    try {
      // Simulate creation in demo mode
      if (isDemoMode) {
        // Ensure all required fields are present with fallbacks
        const newGuest: Guest = {
          id: `mock-guest-${Date.now()}`,
          ...guestData,
          name: guestData.name || 'Unknown Guest',
          email: guestData.email || 'unknown@example.com',
          status: guestData.status || 'pending',
          location: guestData.location ? ensureValidLocation(guestData.location) : defaultLocation,
          created_at: new Date(),
          updated_at: new Date()
        };
        
        mockGuests.push(newGuest);
        return { data: newGuest, error: null };
      }
      
      // Ensure location is valid if provided
      const validatedGuestData = {
        ...guestData,
        location: guestData.location ? ensureValidLocation(guestData.location) : null
      };
      
      const { data, error } = await supabase
        .from('guests')
        .insert([{
          ...validatedGuestData,
          created_at: new Date(),
          updated_at: new Date()
        }])
        .select();
      
      if (error) throw error;
      return { data: data[0] as Guest, error: null };
    } catch (error) {
      console.error('Error creating guest:', error);
      return { data: null, error: error instanceof Error ? error : new Error('Unknown error') };
    }
  },
  
  // Update guest
  async updateGuest(id: string, guestData: Partial<Guest>): Promise<ApiResponse<Guest>> {
    try {
      // Simulate update in demo mode
      if (isDemoMode) {
        const index = mockGuests.findIndex(g => g.id === id);
        if (index === -1) {
          throw new Error(`Guest with ID ${id} not found`);
        }
        
        // Ensure all required fields are maintained
        const updatedGuest: Guest = {
          ...mockGuests[index],
          ...guestData,
          // Ensure these required fields are always present
          id: mockGuests[index]?.id || `mock-guest-${Date.now()}`,
          name: guestData.name || mockGuests[index]?.name || 'Unknown Guest',
          email: guestData.email || mockGuests[index]?.email || 'unknown@example.com',
          status: guestData.status || mockGuests[index]?.status || 'pending',
          location: guestData.location ? ensureValidLocation(guestData.location) : 
                   mockGuests[index]?.location ? ensureValidLocation(mockGuests[index].location) : 
                   defaultLocation,
          created_at: mockGuests[index]?.created_at || new Date(),
          updated_at: new Date()
        };
        
        mockGuests[index] = updatedGuest;
        return { data: updatedGuest, error: null };
      }
      
      const { data, error } = await supabase
        .from('guests')
        .update({
          ...guestData,
          updated_at: new Date()
        })
        .eq('id', id)
        .select();

      if (error) throw error;
      return { data: data[0] as Guest, error: null };
    } catch (error) {
      console.error(`Error updating guest with ID ${id}:`, error);
      return { data: null, error: error instanceof Error ? error : new Error('Unknown error') };
    }
  },
  
  // Delete guest
  async deleteGuest(id: string): Promise<ApiResponse<boolean>> {
    try {
      // Simulate deletion in demo mode
      if (isDemoMode) {
        const index = mockGuests.findIndex(g => g.id === id);
        if (index === -1) {
          throw new Error(`Guest with ID ${id} not found`);
        }
        mockGuests.splice(index, 1);
        return { data: true, error: null };
      }
      
      const { error } = await supabase
        .from('guests')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      return { data: true, error: null };
    } catch (error) {
      console.error(`Error deleting guest with ID ${id}:`, error);
      return { data: false, error: error instanceof Error ? error : new Error('Unknown error') };
    }
  }
};

// Nomination-related functions
export const nominationService = {
  // Get all nominations
  async getAllNominations(): Promise<ApiResponse<Nomination[]>> {
    try {
      // Return mock data in demo mode
      if (isDemoMode) {
        return { data: mockNominations, error: null };
      }
      
      const { data, error } = await supabase
        .from('nominations')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return { data: data as Nomination[], error: null };
    } catch (error) {
      console.error('Error fetching nominations:', error);
      return { data: isDemoMode ? mockNominations : [], error: error instanceof Error ? error : new Error('Unknown error') };
    }
  },
  
  // Get nomination by ID
  async getNominationById(id: string): Promise<ApiResponse<Nomination>> {
    try {
      // Return mock data in demo mode
      if (isDemoMode) {
        const nomination = mockNominations.find(n => n.id === id);
        if (nomination) {
          return { data: nomination, error: null };
        }
        throw new Error(`Nomination with ID ${id} not found`);
      }
      
      const { data, error } = await supabase
        .from('nominations')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      return { data: data as Nomination, error: null };
    } catch (error) {
      console.error(`Error fetching nomination with ID ${id}:`, error);
      return { data: null, error: error instanceof Error ? error : new Error('Unknown error') };
    }
  },
  
  // Create nomination
  async createNomination(nominationData: Omit<Nomination, 'id' | 'created_at' | 'status'>): Promise<ApiResponse<Nomination>> {
    try {
      // Simulate creation in demo mode
      if (isDemoMode) {
        const newNomination: Nomination = {
          id: `mock-nom-${Date.now()}`,
          ...nominationData,
          // Ensure required fields are always present
          nominator_name: nominationData.nominator_name || 'Unknown Nominator',
          nominator_email: nominationData.nominator_email || 'unknown@example.com',
          nominee_name: nominationData.nominee_name || 'Unknown Nominee',
          nominee_email: nominationData.nominee_email || 'unknown@example.com',
          location: nominationData.location ? ensureValidLocation(nominationData.location) : defaultLocation,
          status: 'pending',
          created_at: new Date()
        };
        mockNominations.push(newNomination);
        return { data: newNomination, error: null };
      }
      
      // If location is provided, ensure it's valid
      const validatedNominationData = {
        ...nominationData,
        location: nominationData.location ? ensureValidLocation(nominationData.location) : null
      };
      
      const { data, error } = await supabase
        .from('nominations')
        .insert([{
          ...validatedNominationData,
          status: 'pending' as ApprovalStatus,
          created_at: new Date()
        }])
        .select();
      
      if (error) throw error;
      return { data: data[0] as Nomination, error: null };
    } catch (error) {
      console.error('Error creating nomination:', error);
      return { data: null, error: error instanceof Error ? error : new Error('Unknown error') };
    }
  },
  
  // Update nomination status
  async updateNominationStatus(id: string, status: ApprovalStatus): Promise<ApiResponse<Nomination>> {
    try {
      // Simulate update in demo mode
      if (isDemoMode) {
        const index = mockNominations.findIndex(n => n.id === id);
        if (index === -1) {
          throw new Error(`Nomination with ID ${id} not found`);
        }
        
        // Ensure we maintain all required fields and proper types
        const updatedNomination: Nomination = {
          ...mockNominations[index],
          status,
          // Ensure these required fields are always present
          id: mockNominations[index]?.id || `mock-nom-${Date.now()}`,
          nominator_name: mockNominations[index]?.nominator_name || 'Unknown Nominator',
          nominator_email: mockNominations[index]?.nominator_email || 'unknown@example.com',
          nominee_name: mockNominations[index]?.nominee_name || 'Unknown Nominee',
          nominee_email: mockNominations[index]?.nominee_email || 'unknown@example.com',
          created_at: mockNominations[index]?.created_at || new Date()
        };
        
        mockNominations[index] = updatedNomination;
        
        return { data: updatedNomination, error: null };
      }  
      const { data, error } = await supabase
        .from('nominations')
        .update({ 
          status,
          updated_at: new Date()
        })
        .eq('id', id)
        .select();
      
      if (error) throw error;
      return { data: data[0] as Nomination, error: null };
    } catch (error) {
      console.error(`Error updating nomination status for ID ${id}:`, error);
      return { data: null, error: error instanceof Error ? error : new Error('Unknown error') };
    }
  },
  
  // Delete nomination
  async deleteNomination(id: string): Promise<ApiResponse<boolean>> {
    try {
      // Simulate deletion in demo mode
      if (isDemoMode) {
        const index = mockNominations.findIndex(n => n.id === id);
        if (index === -1) {
          throw new Error(`Nomination with ID ${id} not found`);
        }
        mockNominations.splice(index, 1);
        return { data: true, error: null };
      }
      
      const { error } = await supabase
        .from('nominations')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      return { data: true, error: null };
    } catch (error) {
      console.error(`Error deleting nomination with ID ${id}:`, error);
      return { data: false, error: error instanceof Error ? error : new Error('Unknown error') };
    }
  },
  
  // Convert nomination to target guest
  async convertToTargetGuest(id: string): Promise<ApiResponse<Application>> {
    try {
      // First get the nomination
      const nominationResponse = await this.getNominationById(id);
      
      if (!nominationResponse.data) {
        throw new Error(`Nomination with ID ${id} not found`);
      }
      
      const nomination = nominationResponse.data;
      
      // Create application from nomination data
      const applicationData: Omit<Application, 'id' | 'created_at'> = {
        applicant_name: nomination.nominee_name,
        applicant_email: nomination.nominee_email,
        company: nomination.nominee_company || '',
        title: nomination.nominee_title || '',
        bio: nomination.reason || '',
        // Ensure location is valid or use default
        location: nomination.location ? ensureValidLocation(nomination.location) : defaultLocation,
        status: 'pending'
      };
      
      // Create the application
      const applicationResponse = await applicationService.createApplication(applicationData);
      
      if (!applicationResponse.data) {
        throw new Error('Failed to create application from nomination');
      }
      
      // Update nomination status to indicate conversion
      await this.updateNominationStatus(id, 'approved');
      
      return { data: applicationResponse.data, error: null };
    } catch (error) {
      console.error(`Error converting nomination ${id} to application:`, error);
      return { data: null, error: error instanceof Error ? error : new Error('Unknown error') };
    }
  }
};

// Application-related functions
export const applicationService = {
  // Get all applications
  async getAllApplications(): Promise<ApiResponse<Application[]>> {
    try {
      // Return mock data in demo mode
      if (isDemoMode) {
        return { data: mockApplications, error: null };
      }
      
      const { data, error } = await supabase
        .from('applications')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return { data: data as Application[], error: null };
    } catch (error) {
      console.error('Error fetching applications:', error);
      return { data: isDemoMode ? mockApplications : [], error: error instanceof Error ? error : new Error('Unknown error') };
    }
  },
  
  // Get application by ID
  async getApplicationById(id: string): Promise<ApiResponse<Application>> {
    try {
      // Return mock data in demo mode
      if (isDemoMode) {
        const application = mockApplications.find(a => a.id === id);
        if (application) {
          return { data: application, error: null };
        }
        throw new Error(`Application with ID ${id} not found`);
      }
      
      const { data, error } = await supabase
        .from('applications')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      return { data: data as Application, error: null };
    } catch (error) {
      console.error(`Error fetching application with ID ${id}:`, error);
      return { data: null, error: error instanceof Error ? error : new Error('Unknown error') };
    }
  },
    // Create application
  async createApplication(data: Omit<Application, 'id' | 'created_at'>): Promise<ApiResponse<Application>> {
    try {
      // Simulate creation in demo mode
      if (isDemoMode) {
        const newApplication: Application = {
          id: `mock-app-${Date.now()}`,
          ...data,
          // Ensure required fields are always present
          applicant_name: data.applicant_name || 'Unknown Applicant',
          applicant_email: data.applicant_email || 'unknown@example.com',
          status: data.status || 'pending',
          created_at: new Date()
        };
        mockApplications.push(newApplication);
        return { data: newApplication, error: null };
      }
      
      // If location is provided, ensure it's valid
      const validatedData = {
        ...data,
        location: data.location ? ensureValidLocation(data.location) : null
      };
      
      const { data: responseData, error } = await supabase
        .from('applications')
        .insert([{
          ...validatedData,
          status: 'pending' as ApprovalStatus,
          created_at: new Date()
        }])
        .select();
      
      if (error) throw error;
      return { data: responseData[0] as Application, error: null };
    } catch (error) {
      console.error('Error creating application:', error);
      return { data: null, error: error instanceof Error ? error : new Error('Unknown error') };
    }
  },
  
  // Update application status
  async updateApplicationStatus(id: string, status: ApprovalStatus): Promise<ApiResponse<Application>> {
    try {
      // Simulate update in demo mode
      if (isDemoMode) {
        const index = mockApplications.findIndex(a => a.id === id);
        if (index === -1) {
          throw new Error(`Application with ID ${id} not found`);
        }
        
        // Ensure we maintain all required fields and proper types
        const updatedApplication: Application = {
          ...mockApplications[index],
          status,
          // Ensure these required fields are always present
          id: mockApplications[index]?.id || `mock-app-${Date.now()}`,
          applicant_name: mockApplications[index]?.applicant_name || 'Unknown Applicant',
          applicant_email: mockApplications[index]?.applicant_email || 'unknown@example.com',
          created_at: mockApplications[index]?.created_at || new Date()
        };
        
        mockApplications[index] = updatedApplication;
        
        return { data: updatedApplication, error: null };
      }
      
      const { data, error } = await supabase
        .from('applications')
        .update({ 
          status,
          updated_at: new Date()
        })
        .eq('id', id)
        .select();
      
      if (error) throw error;
      return { data: data[0] as Application, error: null };
    } catch (error) {
      console.error(`Error updating application status for ID ${id}:`, error);
      return { data: null, error: error instanceof Error ? error : new Error('Unknown error') };
    }
  },
  
  // Delete application
  async deleteApplication(id: string): Promise<ApiResponse<boolean>> {
    try {
      // Simulate deletion in demo mode
      if (isDemoMode) {
        const index = mockApplications.findIndex(a => a.id === id);
        if (index === -1) {
          throw new Error(`Application with ID ${id} not found`);
        }
        mockApplications.splice(index, 1);
        return { data: true, error: null };
      }
      
      const { error } = await supabase
        .from('applications')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      return { data: true, error: null };
    } catch (error) {
      console.error(`Error deleting application with ID ${id}:`, error);
      return { data: false, error: error instanceof Error ? error : new Error('Unknown error') };
    }
  },
  
  // Convert application to guest
  async convertToGuest(id: string): Promise<ApiResponse<Guest>> {
    try {
      // First get the application
      const applicationResponse = await this.getApplicationById(id);
      
      if (!applicationResponse.data) {
        throw new Error(`Application with ID ${id} not found`);
      }
      
      const application = applicationResponse.data;
      
      // Create guest from application data
      const guestData = {
        name: application.applicant_name,
        email: application.applicant_email,
        company: application.company || '',
        title: application.title || '',
        bio: application.bio || '',
        // Ensure location is valid or use default
        location: application.location ? ensureValidLocation(application.location) : defaultLocation,
        status: 'pending' as GuestStatus
      };
      
      // Create the guest
      const guestResponse = await guestService.createGuest(guestData);
      
      if (!guestResponse.data) {
        throw new Error('Failed to create guest from application');
      }
      
      // Update application status to indicate conversion
      await this.updateApplicationStatus(id, 'approved');
      
      return { data: guestResponse.data, error: null };
    } catch (error) {
      console.error(`Error converting application ${id} to guest:`, error);
      return { data: null, error: error instanceof Error ? error : new Error('Unknown error') };
    }
  }
}
